import React, { useState } from 'react';
import { Activity, BarChart3, LucideIcon, Megaphone, Package, Users } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { Connection } from '../../../../common/hooks/use-home-api.hook';
import { agentName } from '../agents';
import {
    AgentTemplate,
    TEMPLATE_CATEGORIES,
    TemplateCategory,
    missingFor,
    templateKey,
    templatesIn,
} from '../templates';
import { AgentAvatar } from './AgentAvatar';

/**
 * "Start from a ready-made agent" — Home's answer to the empty box.
 *
 * Each card is a job the team already knows how to do: who does it (their
 * faces), what it needs connected, and one line on what comes back. Picking
 * one hands its brief to the composer; nothing runs until the vendor sends it.
 * Drawn in the neutral language of the rest of the backoffice — a grey glyph
 * on a quiet tile where a consumer product would put an illustration.
 */

const CATEGORY_ICONS: Record<TemplateCategory, LucideIcon> = {
    monitoring: Activity,
    leads: Users,
    content: Megaphone,
    catalogue: Package,
    reporting: BarChart3,
};

/** Shown before "Show all": two rows on a wide screen. */
const FIRST = 6;

export const TemplatesGallery: React.FC<{
    connections: Connection[] | null;
    onPick: (template: AgentTemplate, brief: string) => void;
}> = ({ connections, onPick }) => {
    const { t } = useTranslation();
    const [category, setCategory] = useState<TemplateCategory | 'all'>('all');
    const [expanded, setExpanded] = useState(false);

    const all = templatesIn(category);
    const shown = expanded ? all : all.slice(0, FIRST);

    const pill = (id: TemplateCategory | 'all', label: string) => (
        <button
            key={id}
            type="button"
            onClick={() => { setCategory(id); setExpanded(false); }}
            aria-pressed={category === id}
            className={cn(
                'shrink-0 rounded-full border px-3 py-1 text-xs transition-colors',
                category === id
                    ? 'border-foreground text-foreground'
                    : 'bg-background text-muted-foreground hover:border-foreground/30 hover:text-foreground',
            )}
        >
            {label}
        </button>
    );

    return (
        <section className="mx-auto w-full max-w-5xl px-4 pb-10">
            <div className="mb-1 flex items-baseline justify-between gap-3">
                <h2 className="text-sm font-medium">{t('AGENTS.TEMPLATES.TITLE', 'Start from a ready-made agent')}</h2>
                <span className="text-xs text-muted-foreground">
                    {t('AGENTS.TEMPLATES.COUNT', { count: all.length, defaultValue: '{{count}} agents' })}
                </span>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">
                {t('AGENTS.TEMPLATES.SUBTITLE', 'Jobs your team already knows how to do. Pick one, adapt it, send it.')}
            </p>

            <div className="mb-3 flex gap-1.5 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {pill('all', t('AGENTS.TEMPLATES.ALL', 'All'))}
                {TEMPLATE_CATEGORIES.map((c) => pill(c, t(`AGENTS.TEMPLATES.CATEGORY.${c.toUpperCase()}`)))}
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((template) => {
                    const Icon = CATEGORY_ICONS[template.category];
                    const key = templateKey(template.id);
                    const missing = missingFor(template, connections);

                    return (
                        <button
                            key={template.id}
                            type="button"
                            onClick={() => onPick(template, String(t(`${key}.PROMPT`)))}
                            className="group flex flex-col overflow-hidden rounded-2xl border bg-card text-start transition-colors hover:border-foreground/25"
                        >
                            <div className="relative flex h-24 items-center justify-center border-b bg-muted/40">
                                <Icon className="size-8 text-muted-foreground/60 transition-colors group-hover:text-muted-foreground" strokeWidth={1.25} aria-hidden />
                                <span className="absolute start-3 bottom-2.5 flex -space-x-1.5 rtl:space-x-reverse">
                                    {template.agents.map((a) => <AgentAvatar key={a} agent={a} size="sm" />)}
                                </span>
                            </div>

                            <div className="flex flex-1 flex-col gap-1 p-3.5">
                                <span className="text-xs text-muted-foreground">
                                    {t(`AGENTS.TEMPLATES.CATEGORY.${template.category.toUpperCase()}`)}
                                </span>
                                <span className="text-sm font-medium leading-snug">{t(`${key}.TITLE`)}</span>
                                <span className="line-clamp-2 text-xs leading-relaxed text-muted-foreground">{t(`${key}.DESC`)}</span>
                                <span className="mt-auto pt-2 text-xs text-muted-foreground">
                                    {missing.length
                                        ? (
                                            <span className="flex items-center gap-1.5">
                                                <span className="size-1.5 rounded-full bg-warning" aria-hidden />
                                                {t('AGENTS.TEMPLATES.NEEDS', {
                                                    names: missing.map((m) => t(`AGENTS.CONNECT.ITEM.${m.toUpperCase()}.NAME`)).join(', '),
                                                    defaultValue: 'Needs {{names}}',
                                                })}
                                            </span>
                                        )
                                        : template.agents.map((a) => agentName(t, a)).join(' · ')}
                                </span>
                            </div>
                        </button>
                    );
                })}
            </div>

            {all.length > FIRST && (
                <div className="mt-3 flex justify-center">
                    <button
                        type="button"
                        onClick={() => setExpanded((v) => !v)}
                        className="rounded-full border bg-background px-3.5 py-1.5 text-xs text-muted-foreground transition-colors hover:border-foreground/30 hover:text-foreground"
                    >
                        {expanded
                            ? t('AGENTS.TEMPLATES.SHOW_FEWER', 'Show fewer')
                            : t('AGENTS.TEMPLATES.SHOW_ALL', { count: all.length, defaultValue: 'Show all {{count}}' })}
                    </button>
                </div>
            )}
        </section>
    );
};
